import React, { useState, useEffect, useMemo } from "react";
import styles from "./Dashboard.module.scss";
import moment from "moment";
import { db } from "../../../firebase";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { MdModeEdit } from "react-icons/md";
import { EditBgOverviewModal } from "../../PlayerProfileDetail/background-overview/EditBgOverviewModal";
import Header from "../../header/header";
import { CSVLink } from "react-csv";
import { FiDownload } from "react-icons/fi";

const stripAauTeamLocation = (aauTeam) => {
  if (!aauTeam || typeof aauTeam !== "string") return "";
  return aauTeam
    .replace(/\s*\([A-Z]{2,3}\)$/i, "")
    .replace(/,\s*[A-Z]{2,3}$/i, "")
    .trim();
};

/** Normalize quotes and repair doubled-quote artifacts so Sheets shows nicknames correctly. */
const sanitizeExportText = (value) => {
  if (value === undefined || value === null) return "";
  let s = String(value).trim();
  s = s.replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"');
  s = s.replace(/[\u2018\u2019\u2032]/g, "'");
  // CSV escape sequences stored in Firebase (e.g. ""Pop"" -> "Pop")
  while (s.includes('""')) {
    s = s.replace(/""/g, '"');
  }
  return s;
};

const buildPPExportRow = (playerData, playerId, aauTeam) => {
  const firstName = sanitizeExportText(playerData.FirstName);
  const lastName = sanitizeExportText(playerData.LastName);
  const title = sanitizeExportText(playerData.Title);
  const playerProfile =
    title || [firstName, lastName].filter(Boolean).join(" ").trim();

  return {
    "Player Profile": playerProfile,
    "aauTeam(without state)": stripAauTeamLocation(aauTeam),
    id: playerId,
    FirstName: firstName,
    LastName: lastName,
    Age: formatExportAge(playerData.Age),
    Country: sanitizeExportText(playerData.Country),
    Team: sanitizeExportText(playerData.Team),
    aauTeam: sanitizeExportText(aauTeam),
  };
};

const formatExportAge = (age) => {
  if (age === undefined || age === null || age === "" || Number.isNaN(Number(age))) {
    return "";
  }
  const n = Number(age);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const PP_EXPORT_HEADERS = [
  { label: "Player Profile", key: "Player Profile" },
  { label: "aauTeam(without state)", key: "aauTeam(without state)" },
  { label: "id", key: "id" },
  { label: "FirstName", key: "FirstName" },
  { label: "LastName", key: "LastName" },
  { label: "Age", key: "Age" },
  { label: "Country", key: "Country" },
  { label: "Team", key: "Team" },
  { label: "aauTeam", key: "aauTeam" },
];

export default function BackgroundOverviewFeed() {
  const [overviewFeed, setOverviewFeed] = useState([]);
  const [sortOrder, setSortOrder] = useState(null);
  const [selectedEditItem, setSelectedEditItem] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [ppCsvData, setPpCsvData] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [hasBO, setHasBO] = useState(false);
  const [hasSO, setHasSO] = useState(false);

  const getOverviewFeed = async () => {
    try {
      // Batch fetch both collections at once
      const [overviewSnapshot, playerProfileSnapshot] = await Promise.all([
        db.collection("background-overview").orderBy("createdAt", "desc").get(),
        db.collection("player-profile").get()
      ]);

      // Create a map of player profiles for quick lookup
      const profilesMap = {};
      playerProfileSnapshot.docs.forEach(doc => {
        profilesMap[doc.id] = doc.data().Title || "Null";
      });

      // Process overview feed with batched player profile titles
      const feed = overviewSnapshot.docs.map(doc => {
        let data = doc.data();
        data.title = data.title || profilesMap[data.playerProfile] || "Null"; // Use cached title if exists
        data.id = doc.id;
        return data;
      });

      setOverviewFeed(feed);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const sortedFilteredFeed = useMemo(() => {
    let filteredFeed = overviewFeed;

    // Apply filter if selected
    if (filterType) {
      filteredFeed = overviewFeed.filter(item => (filterType === "IBO" ? item.IBO : !item.IBO));
    }

    // Apply sorting if selected
    if (sortOrder) {
      filteredFeed = [...filteredFeed].sort((a, b) => {
        const titleA = a.title || "Null";
        const titleB = b.title || "Null";
        return sortOrder === "asc" ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
      });
    }

    return filteredFeed;
  }, [overviewFeed, sortOrder, filterType]);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleEditClick = (item) => {
    setSelectedEditItem(item);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditItem(null);
    getOverviewFeed();
  };

  const handleTypeFilterClick = () => {
    if (filterType === "IBO") setFilterType("BO");
    else if (filterType === "BO") setFilterType(null); // Remove filter
    else setFilterType("IBO");
  };

  const fetchAllPlayersForCSV = async () => {
    setCsvLoading(true);
    try {
      // Fetch all players
      const playersSnapshot = await db.collection("player-profile").get();
      
      // Fetch all background overviews and create a set of player IDs
      const boSnapshot = await db.collection("background-overview").get();
      const boPlayerIds = new Set();
      boSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.playerProfile) {
          boPlayerIds.add(data.playerProfile);
        }
      });

      // Fetch all scouting overviews and create a set of player IDs
      const soSnapshot = await db.collection("scouting-overview").get();
      const soPlayerIds = new Set();
      soSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.playerProfile) {
          soPlayerIds.add(data.playerProfile);
        }
      });

      const csvDataArray = [];
      const ppExportRows = [];

      playersSnapshot.docs.forEach((doc) => {
        const playerData = doc.data();
        const playerId = doc.id;
        const hasBO = boPlayerIds.has(playerId);
        const aauTeam = playerData.aauTeam || "";

        csvDataArray.push({
          "Player ID": playerId,
          "Player Full Name": playerData.Title || "N/A",
          "Has BO": hasBO ? "Yes" : "No",
        });

        ppExportRows.push(buildPPExportRow(playerData, playerId, aauTeam));
      });

      ppExportRows.sort((a, b) => {
        const byLast = (a.LastName || "").localeCompare(b.LastName || "");
        if (byLast !== 0) return byLast;
        return (a.FirstName || "").localeCompare(b.FirstName || "");
      });

      setCsvData(csvDataArray);
      setPpCsvData(ppExportRows);
      
      // Update indicators
      setHasBO(boPlayerIds.size > 0);
      setHasSO(soPlayerIds.size > 0);
    } catch (error) {
      console.error("Error fetching data for CSV:", error);
    } finally {
      setCsvLoading(false);
    }
  };

  useEffect(() => {
    getOverviewFeed();
    fetchAllPlayersForCSV(); // Fetch data on component mount
  }, []);

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.feedContainer}>
        <div className={styles.feed}>
          <div className={styles.feedHeader}>
            <p>Overview Feed</p>
            <div className={styles.csvExportContainer}>
              <div className={styles.indicators}>
                {hasBO && <span className={styles.indicator} title="Has Background Overview">BO</span>}
                {hasSO && <span className={styles.indicator} title="Has Scouting Overview">SO</span>}
              </div>
              {csvLoading ? (
                <button className={styles.csvButton} disabled>
                  Loading...
                </button>
              ) : (
                <>
                  <CSVLink
                    data={csvData}
                    filename={`player-database-export-${moment().format("YYYY-MM-DD")}.csv`}
                    headers={[
                      { label: "Player ID", key: "Player ID" },
                      { label: "Player Full Name", key: "Player Full Name" },
                      { label: "Has BO", key: "Has BO" },
                    ]}
                    className={styles.csvButton}
                  >
                    <FiDownload /> Export CSV
                  </CSVLink>
                  <CSVLink
                    data={ppCsvData}
                    filename={`all-player-profiles-${moment().format("YYYY-MM-DD")}.tsv`}
                    headers={PP_EXPORT_HEADERS}
                    separator={"\t"}
                    enclosingCharacter=""
                    unicodeBOM
                    className={styles.csvButton}
                  >
                    <FiDownload /> Export ALL PP
                  </CSVLink>
                </>
              )}
            </div>
          </div>
          <div className={styles.tableContainer} style={{ maxHeight: "40rem", overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Date Created</th>
                  <th onClick={toggleSortOrder} style={{ cursor: "pointer" }}>
                    Player Profile {sortOrder ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th>Completion %</th>
                  <th onClick={handleTypeFilterClick} style={{ cursor: "pointer" }}>
                    Type {filterType ? `(${filterType})` : ""}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedFilteredFeed.map((item, index) => (
                  <tr key={index}>
                    <td>{moment.unix(item.createdAt).format("l")}</td>
                    <td>
                      {item.playerProfile ? (
                        <Link to={`/player-profile/detail/${item.playerProfile}`}>
                          {item.title || "Null"}
                        </Link>
                      ) : (
                        "No Profile"
                      )}
                    </td>
                    <td>-</td>
                    <td>{item.IBO ? "IBO" : "BO"}</td>
                    <td>
                      <MdModeEdit onClick={() => handleEditClick(item)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {isEditModalOpen && (
          <EditBgOverviewModal
            show={isEditModalOpen}
            handleClose={handleEditModalClose}
            bgOverviewData={selectedEditItem}
          />
        )}
      </div>
    </div>
  );
}
